import React, { useState, useEffect } from 'react';
import { useNavigate, Link } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import axios from 'axios';
import {
  Container,
  Paper,
  TextField,
  Button,
  Typography,
  Box,
  Alert,
  Grid,
  MenuItem,
  LinearProgress,
  Chip,
  Divider,
  Card,
  CardContent,
  Step,
  Stepper,
  StepLabel,
  CircularProgress,
  Checkbox,
} from '@mui/material';
import {
  School,
  PersonAdd,
  CheckCircle,
  ArrowForward,
  Home,
  Cancel,
} from '@mui/icons-material';

const API_URL = process.env.REACT_APP_API_URL || 'https://smartvidesa.lk/api';

const Register = () => {
  const [formData, setFormData] = useState({
    initial_name: '',
    name_with_initials: '',
    full_name: '',
    username: '',
    birthday: '',
    phone: '',
    parent_phone: '',
    address: '',
    ol_year: '',
    subjects: [], // NEW: Mathematics and/or Science
    whatsapp_number: '',
    email: '',
    password: '',
    password_confirmation: '',
    class_ids: [],
  });
  const [classes, setClasses] = useState([]);
  const [calculatedGrade, setCalculatedGrade] = useState(null);
  const [error, setError] = useState('');
  const [loading, setLoading] = useState(false);
  const [activeStep, setActiveStep] = useState(0);
  const [usernameError, setUsernameError] = useState('');
  const [usernameChecking, setUsernameChecking] = useState(false);
  const [usernameAvailable, setUsernameAvailable] = useState(null);
  const { register } = useAuth();
  const navigate = useNavigate();

  const steps = ['Personal Info', 'Contact Details', 'Account Setup'];

  // Fetch classes on component mount
  useEffect(() => {
    fetchClasses();
  }, []);

  // Debounced username validation
  useEffect(() => {
    if (!formData.username) {
      setUsernameError('');
      setUsernameAvailable(null);
      return;
    }

    // Check for spaces immediately
    if (formData.username.includes(' ')) {
      setUsernameError('Username cannot contain spaces. Use underscores instead.');
      setUsernameAvailable(false);
      return;
    }

    // Check for invalid characters
    if (!/^[a-zA-Z0-9_]+$/.test(formData.username)) {
      setUsernameError('Username can only contain letters, numbers, and underscores.');
      setUsernameAvailable(false);
      return;
    }

    // Debounce API call
    const timer = setTimeout(() => {
      checkUsernameAvailability(formData.username);
    }, 500);

    return () => clearTimeout(timer);
  }, [formData.username]);

  const fetchClasses = async () => {
    try {
      const response = await axios.get(`${API_URL}/classes`);
      setClasses(response.data);
    } catch (error) {
    }
  };

  const checkUsernameAvailability = async (username) => {
    if (username.length < 3) {
      setUsernameError('Username must be at least 3 characters');
      setUsernameAvailable(false);
      return;
    }

    setUsernameChecking(true);
    setUsernameError('');

    try {
      const response = await axios.post(`${API_URL}/check-username`, { username });
      
      if (response.data.available) {
        setUsernameAvailable(true);
        setUsernameError('');
      } else {
        setUsernameAvailable(false);
        setUsernameError(response.data.message);
      }
    } catch (error) {
      setUsernameError('Unable to verify username availability');
      setUsernameAvailable(null);
    } finally {
      setUsernameChecking(false);
    }
  };

  // Calculate progress
  const calculateProgress = () => {
    const fields = Object.keys(formData);
    const filledFields = fields.filter(key => formData[key] !== '').length;
    return (filledFields / fields.length) * 100;
  };

  const handleChange = (e) => {
    const { name, value } = e.target;
    
    // Prevent spaces in username
    if (name === 'username') {
      // Remove spaces and convert to lowercase
      const sanitizedValue = value.replace(/\s/g, '_').toLowerCase();
      setFormData({
        ...formData,
        [name]: sanitizedValue,
      });
      return;
    }
    
    setFormData({
      ...formData,
      [name]: value,
    });

    // Auto-calculate grade when O/L year changes
    if (name === 'ol_year') {
      const currentYear = new Date().getFullYear();
      const olYear = 2000 + parseInt(value); // Convert 26 to 2026
      const yearsUntilOL = olYear - currentYear;
      const grade = 11 - yearsUntilOL;
      setCalculatedGrade(Math.max(6, Math.min(11, grade)));
    }
  };

  const handleSubjectToggle = (subject) => {
    setFormData(prev => ({
      ...prev,
      subjects: prev.subjects.includes(subject)
        ? prev.subjects.filter(s => s !== subject)
        : [...prev.subjects, subject]
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    // Validation
    if (formData.password !== formData.password_confirmation) {
      setError('Passwords do not match');
      return;
    }

    if (formData.password.length < 8) {
      setError('Password must be at least 8 characters');
      return;
    }

    if (formData.subjects.length === 0) {
      setError('Please select at least one subject (Mathematics or Science)');
      return;
    }

    if (!usernameAvailable) {
      setError('Please choose a valid and available username');
      return;
    }

    if (formData.username.includes(' ')) {
      setError('Username cannot contain spaces');
      return;
    }

    setLoading(true);

    try {
      // Register as student only
      await register({
        initial_name: formData.initial_name,
        name_with_initials: formData.name_with_initials,
        full_name: formData.full_name,
        username: formData.username,
        birthday: formData.birthday,
        phone: formData.phone,
        parent_phone: formData.parent_phone,
        address: formData.address,
        ol_year: formData.ol_year,
        subjects: JSON.stringify(formData.subjects), // Send as JSON string
        whatsapp_number: formData.whatsapp_number,
        email: formData.email,
        password: formData.password,
        password_confirmation: formData.password_confirmation,
        role: 'student', // Fixed role for public registration
        class_id: formData.class_ids[0] || null,
        secondary_class_id: formData.class_ids[1] || null,
      });
      navigate('/dashboard');
    } catch (err) {
      setError(
        err.response?.data?.message ||
          err.response?.data?.errors?.email?.[0] ||
          'Registration failed. Please try again.'
      );
    } finally {
      setLoading(false);
    }
  };

  return (
    <Box
      sx={{
        minHeight: '100vh',
        display: 'flex',
        alignItems: 'center',
        background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
        py: 6,
        position: 'relative',
        overflow: 'hidden',
        '&::before': {
          content: '""',
          position: 'absolute',
          top: 0,
          left: 0,
          right: 0,
          bottom: 0,
          background: 'url("data:image/svg+xml,%3Csvg width=\'60\' height=\'60\' viewBox=\'0 0 60 60\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cg fill=\'none\' fill-rule=\'evenodd\'%3E%3Cg fill=\'%23ffffff\' fill-opacity=\'0.05\'%3E%3Cpath d=\'M36 34v-4h-2v4h-4v2h4v4h2v-4h4v-2h-4zm0-30V0h-2v4h-4v2h4v4h2V6h4V4h-4zM6 34v-4H4v4H0v2h4v4h2v-4h4v-2H6zM6 4V0H4v4H0v2h4v4h2V6h4V4H6z\'/%3E%3C/g%3E%3C/g%3E%3C/svg%3E")',
          opacity: 0.5,
        },
      }}
    >
      <Container component="main" maxWidth="md" sx={{ position: 'relative', zIndex: 1 }}>
        {/* Header Card */}
        <Card
          sx={{
            mb: 3,
            background: 'rgba(255, 255, 255, 0.95)',
            backdropFilter: 'blur(10px)',
            borderRadius: 3,
            boxShadow: '0 8px 32px rgba(0,0,0,0.1)',
          }}
        >
          <CardContent sx={{ textAlign: 'center', py: 3 }}>
            <Box
              sx={{
                display: 'inline-flex',
                p: 2,
                borderRadius: '50%',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                mb: 2,
                animation: 'float 3s ease-in-out infinite',
                '@keyframes float': {
                  '0%, 100%': { transform: 'translateY(0px)' },
                  '50%': { transform: 'translateY(-10px)' },
                },
              }}
            >
              <School sx={{ fontSize: 48, color: 'white' }} />
            </Box>
            <Typography 
              component="h1" 
              variant="h3" 
              fontWeight="900" 
              sx={{
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                WebkitBackgroundClip: 'text',
                WebkitTextFillColor: 'transparent',
                mb: 1,
              }}
            >
              Join Videsa Today! 🎓
            </Typography>
            <Typography variant="h6" color="textSecondary" sx={{ mb: 2 }}>
              Create your student account and start your learning journey
            </Typography>
            
            {/* Progress Indicator */}
            <Box sx={{ maxWidth: 600, mx: 'auto', mt: 3 }}>
              <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 1 }}>
                <Typography variant="body2" fontWeight="bold" color="primary">
                  Registration Progress
                </Typography>
                <Chip 
                  label={`${Math.round(calculateProgress())}% Complete`}
                  size="small"
                  sx={{ 
                    bgcolor: '#667eea20',
                    color: '#667eea',
                    fontWeight: 'bold',
                  }}
                />
              </Box>
              <LinearProgress 
                variant="determinate" 
                value={calculateProgress()} 
                sx={{
                  height: 8,
                  borderRadius: 4,
                  bgcolor: '#e0e0e0',
                  '& .MuiLinearProgress-bar': {
                    borderRadius: 4,
                    background: 'linear-gradient(90deg, #667eea 0%, #764ba2 100%)',
                  },
                }}
              />
            </Box>
          </CardContent>
        </Card>

        <Paper
          elevation={10}
          sx={{
            padding: { xs: 3, md: 5 },
            borderRadius: 3,
            background: 'rgba(255, 255, 255, 0.98)',
            backdropFilter: 'blur(10px)',
            boxShadow: '0 20px 60px rgba(0,0,0,0.15)',
          }}
        >

          {error && (
            <Alert severity="error" sx={{ mt: 2, mb: 2 }}>
              {error}
            </Alert>
          )}

          <Box component="form" onSubmit={handleSubmit}>
            {/* Personal Information Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  1
                </Box>
                <Typography variant="h6" fontWeight="bold" color="primary">
                  Personal Information
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="initial_name"
                  label="Initial Name"
                  name="initial_name"
                  autoFocus
                  value={formData.initial_name}
                  onChange={handleChange}
                  placeholder="e.g., J. Doe"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="name_with_initials"
                  label="Name with Initials"
                  name="name_with_initials"
                  value={formData.name_with_initials}
                  onChange={handleChange}
                  placeholder="e.g., J.D. Smith"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="full_name"
                  label="Full Name"
                  name="full_name"
                  autoComplete="name"
                  value={formData.full_name}
                  onChange={handleChange}
                  placeholder="e.g., John David Smith"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="username"
                  label="Username"
                  name="username"
                  autoComplete="username"
                  value={formData.username}
                  onChange={handleChange}
                  placeholder="e.g., john_smith"
                  error={!!usernameError}
                  helperText={
                    usernameChecking
                      ? 'Checking availability...'
                      : usernameError
                      ? usernameError
                      : usernameAvailable
                      ? '✓ Username is available!'
                      : 'Use only letters, numbers, and underscores'
                  }
                  InputProps={{
                    endAdornment: usernameChecking ? (
                      <CircularProgress size={20} />
                    ) : usernameAvailable === true ? (
                      <CheckCircle sx={{ color: '#10b981' }} />
                    ) : usernameAvailable === false ? (
                      <Cancel sx={{ color: '#ef4444' }} />
                    ) : null,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                    '& .MuiFormHelperText-root': {
                      color: usernameAvailable === true
                        ? '#10b981'
                        : usernameAvailable === false
                        ? '#ef4444'
                        : undefined,
                      fontWeight: usernameAvailable !== null ? 600 : 400,
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="birthday"
                  label="Birthday"
                  name="birthday"
                  type="date"
                  value={formData.birthday}
                  onChange={handleChange}
                  InputLabelProps={{
                    shrink: true,
                  }}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  select
                  id="ol_year"
                  label="O/L Examination Year"
                  name="ol_year"
                  value={formData.ol_year}
                  onChange={handleChange}
                  helperText="Select your O/L exam year"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                >
                  <MenuItem value="26">2026 - Grade 11 (O/L This Year)</MenuItem>
                  <MenuItem value="27">2027 - Grade 10</MenuItem>
                  <MenuItem value="28">2028 - Grade 9</MenuItem>
                  <MenuItem value="29">2029 - Grade 8</MenuItem>
                  <MenuItem value="30">2030 - Grade 7</MenuItem>
                  <MenuItem value="31">2031 - Grade 6</MenuItem>
                </TextField>
              </Grid>

              {/* Auto-calculated Grade Display */}
              {calculatedGrade && (
                <Grid item xs={12} sm={6}>
                  <Alert severity="info" icon={false} sx={{ height: '100%', display: 'flex', alignItems: 'center', borderRadius: 2 }}>
                    <Typography variant="body1" component="div">
                      Current Grade: <Chip label={`Grade ${calculatedGrade}`} color="primary" sx={{ fontWeight: 700, ml: 1 }} />
                    </Typography>
                  </Alert>
                </Grid>
              )}

              {/* Subject Selection - Visual Cards */}
              <Grid item xs={12}>
                <Typography variant="subtitle1" fontWeight="600" sx={{ mb: 2, color: '#667eea' }}>
                  Select Subject(s) *
                </Typography>
                
                <Grid container spacing={2}>
                  {/* Mathematics Card */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={formData.subjects.includes('Mathematics') ? 8 : 2}
                      onClick={() => handleSubjectToggle('Mathematics')}
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        textAlign: 'center',
                        border: formData.subjects.includes('Mathematics') 
                          ? '3px solid #667eea' 
                          : '2px solid #e0e0e0',
                        background: formData.subjects.includes('Mathematics')
                          ? 'linear-gradient(135deg, rgba(102, 126, 234, 0.1) 0%, rgba(118, 75, 162, 0.1) 100%)'
                          : '#fff',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      <Typography variant="h2" mb={2}>🔢</Typography>
                      <Typography variant="h6" fontWeight="700" mb={1}>
                        Mathematics
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Algebra, Geometry, Calculus
                      </Typography>
                      {formData.subjects.includes('Mathematics') && (
                        <Chip 
                          label="✓ Selected" 
                          color="primary" 
                          size="small" 
                          sx={{ mt: 2, fontWeight: 700 }}
                        />
                      )}
                    </Paper>
                  </Grid>

                  {/* Science Card */}
                  <Grid item xs={12} sm={6}>
                    <Paper
                      elevation={formData.subjects.includes('Science') ? 8 : 2}
                      onClick={() => handleSubjectToggle('Science')}
                      sx={{
                        p: 3,
                        cursor: 'pointer',
                        textAlign: 'center',
                        border: formData.subjects.includes('Science') 
                          ? '3px solid #10b981' 
                          : '2px solid #e0e0e0',
                        background: formData.subjects.includes('Science')
                          ? 'linear-gradient(135deg, rgba(16, 185, 129, 0.1) 0%, rgba(5, 150, 105, 0.1) 100%)'
                          : '#fff',
                        borderRadius: 3,
                        transition: 'all 0.3s ease',
                        '&:hover': {
                          transform: 'translateY(-6px)',
                          boxShadow: 6,
                        },
                      }}
                    >
                      <Typography variant="h2" mb={2}>🔬</Typography>
                      <Typography variant="h6" fontWeight="700" mb={1}>
                        Science
                      </Typography>
                      <Typography variant="caption" color="text.secondary">
                        Physics, Chemistry, Biology
                      </Typography>
                      {formData.subjects.includes('Science') && (
                        <Chip 
                          label="✓ Selected" 
                          size="small" 
                          sx={{ 
                            mt: 2, 
                            fontWeight: 700,
                            bgcolor: '#10b981',
                            color: '#fff'
                          }}
                        />
                      )}
                    </Paper>
                  </Grid>
                </Grid>

                <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
                  You can select one or both subjects
                </Typography>
              </Grid>

              <Grid item xs={12}>
                
              </Grid>
            </Grid>
            </Box>

            <Divider sx={{ my: 4 }}>
              <Chip label="Contact Information" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }} />
            </Divider>

            {/* Contact Information Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #f093fb 0%, #f5576c 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  2
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#f093fb' }}>
                  Contact Details
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="phone"
                  label="Phone Number"
                  name="phone"
                  type="tel"
                  value={formData.phone}
                  onChange={handleChange}
                  placeholder="e.g., +1234567890"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  id="whatsapp_number"
                  label="WhatsApp Number"
                  name="whatsapp_number"
                  type="tel"
                  value={formData.whatsapp_number}
                  onChange={handleChange}
                  placeholder="e.g., +94771234567"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="parent_phone"
                  label="Parent's Phone Number"
                  name="parent_phone"
                  type="tel"
                  value={formData.parent_phone}
                  onChange={handleChange}
                  placeholder="e.g., +1234567890"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  required
                  fullWidth
                  id="address"
                  label="Address"
                  name="address"
                  multiline
                  rows={3}
                  value={formData.address}
                  onChange={handleChange}
                  placeholder="Enter your full address"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12}>
                <TextField
                  fullWidth
                  id="email"
                  label="Email Address (Optional)"
                  name="email"
                  autoComplete="email"
                  type="email"
                  value={formData.email}
                  onChange={handleChange}
                  placeholder="student@example.com"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
            </Box>

            <Divider sx={{ my: 4 }}>
              <Chip label="Account Security" sx={{ bgcolor: '#f5f5f5', fontWeight: 'bold' }} />
            </Divider>

            {/* Account Security Section */}
            <Box sx={{ mb: 4 }}>
              <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 3 }}>
                <Box
                  sx={{
                    width: 40,
                    height: 40,
                    borderRadius: 2,
                    background: 'linear-gradient(135deg, #4facfe 0%, #00f2fe 100%)',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    color: 'white',
                    fontWeight: 'bold',
                  }}
                >
                  3
                </Box>
                <Typography variant="h6" fontWeight="bold" sx={{ color: '#4facfe' }}>
                  Account Setup
                </Typography>
              </Box>
              <Grid container spacing={2.5}>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="password"
                  label="Password"
                  type="password"
                  id="password"
                  autoComplete="new-password"
                  value={formData.password}
                  onChange={handleChange}
                  helperText="Must be at least 8 characters"
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
              <Grid item xs={12} sm={6}>
                <TextField
                  required
                  fullWidth
                  name="password_confirmation"
                  label="Confirm Password"
                  type="password"
                  id="password_confirmation"
                  autoComplete="new-password"
                  value={formData.password_confirmation}
                  onChange={handleChange}
                  sx={{
                    '& .MuiOutlinedInput-root': {
                      borderRadius: 2,
                      transition: 'all 0.3s ease',
                      '&:hover': {
                        boxShadow: '0 4px 12px rgba(102, 126, 234, 0.15)',
                      },
                      '&.Mui-focused': {
                        boxShadow: '0 4px 20px rgba(102, 126, 234, 0.25)',
                      },
                    },
                  }}
                />
              </Grid>
            </Grid>
            </Box>

            <Button
              type="submit"
              fullWidth
              variant="contained"
              size="large"
              endIcon={loading ? null : <ArrowForward />}
              sx={{
                mt: 4,
                mb: 3,
                py: 2,
                borderRadius: 3,
                fontSize: '1.1rem',
                fontWeight: 'bold',
                background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)',
                boxShadow: '0 8px 20px rgba(102, 126, 234, 0.3)',
                '&:hover': {
                  background: 'linear-gradient(135deg, #764ba2 0%, #667eea 100%)',
                  transform: 'translateY(-2px)',
                  boxShadow: '0 12px 28px rgba(102, 126, 234, 0.4)',
                },
                '&:disabled': {
                  background: '#cccccc',
                },
                transition: 'all 0.3s ease',
              }}
              disabled={loading}
            >
              {loading ? (
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
                  <CircularProgress size={24} sx={{ color: 'white' }} />
                  <span>Creating Your Account...</span>
                </Box>
              ) : (
                'Create My Account'
              )}
            </Button>

            <Box sx={{ textAlign: 'center' }}>
              <Card
                sx={{
                  p: 2.5,
                  background: 'linear-gradient(135deg, #f5f7fa 0%, #c3cfe2 100%)',
                  borderRadius: 2,
                  mb: 3,
                }}
              >
                <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 1, mb: 1 }}>
                  <CheckCircle sx={{ color: '#43e97b', fontSize: 20 }} />
                  <Typography variant="body2" fontWeight="600">
                    Already have an account?
                  </Typography>
                </Box>
                <Button
                  component={Link}
                  to="/login"
                  variant="outlined"
                  sx={{
                    borderColor: '#667eea',
                    color: '#667eea',
                    fontWeight: 'bold',
                    borderRadius: 2,
                    px: 4,
                    '&:hover': {
                      borderColor: '#667eea',
                      bgcolor: '#667eea10',
                    },
                  }}
                >
                  Sign In Instead
                </Button>
              </Card>

              <Button
                component={Link}
                to="/"
                startIcon={<Home />}
                sx={{
                  color: '#667eea',
                  fontWeight: 'bold',
                  '&:hover': {
                    bgcolor: '#667eea10',
                  },
                }}
              >
                Back to Home
              </Button>
            </Box>
          </Box>
        </Paper>
      </Container>
    </Box>
  );
};

export default Register;
